import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/axiosConfig';
import { useModal } from '../contexts/ModalContext';
import AuthContext from '../contexts/AuthContext';
import ImageViewerModal from '../components/ImageViewerModal';
import IssueSliderModal from '../components/IssueSliderModal';
import { toLocalDateString } from '../utils/localDate';
import Pagination from '../components/Pagination';
import { formatDescription } from '../utils/reportDescription';

const FacilityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useModal();
  const { user } = useContext(AuthContext);
  // 지운 기록을 보고 되살리는 것은 기록을 지울 수 있는 사람의 일이다. 서버도
  // 같은 열쇠로 막는다.
  const canManageReports =
    ['OWNER', 'SUPER_ADMIN'].includes(user?.role) ||
    Boolean(user?.permissions?.manage_reports);
  const canManageLeases =
    ['OWNER', 'SUPER_ADMIN'].includes(user?.role) ||
    Boolean(user?.permissions?.manage_leases);
  /** 이 시설의 호실과 지금 쓰는 임차인. 권한이 없으면 부르지 않는다. */
  const [occupancy, setOccupancy] = useState(null);

  // Data State
  const [facility, setFacility] = useState(null);
  const [stats, setStats] = useState({ issues: { total: 0, unresolved: 0, resolved: 0 } });
  // 점검 기록은 한 쪽씩 가져온다. 전부 내려받으면 사진 서명만으로도 느리다.
  const [inspections, setInspections] = useState([]);
  const [reportPage, setReportPage] = useState({ page: 1, total: 0, limit: 20 });
  const [loadingReports, setLoadingReports] = useState(false);
  /** 펼친 기록. { [id]: { loading } | { photoUrls } } */
  const [expanded, setExpanded] = useState({});

  /**
   * 펼칠 때 그 한 건만 가져온다.
   *
   * 목록은 사진을 내지 않는다 -- 주소를 서명하는 값이 건마다 들기 때문이다.
   * 열어 본 것만 값을 치르면 대개 한두 건이다. 한 번 가져온 것은 그대로 둔다.
   */
  const toggleReport = async (reportId) => {
    if (expanded[reportId]) {
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[reportId];
        return next;
      });
      return;
    }

    setExpanded((prev) => ({ ...prev, [reportId]: { loading: true } }));
    try {
      const { data } = await api.get(`/reports/${reportId}`);
      setExpanded((prev) => ({
        ...prev,
        [reportId]: { photoUrls: data?.photoUrls ?? [] },
      }));
    } catch (err) {
      console.error('Failed to load report', err);
      setExpanded((prev) => ({ ...prev, [reportId]: { photoUrls: [] } }));
    }
  };
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);

  // UI/Loading State
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Image Viewer State
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openViewer = (images, index) => {
    // Avoid propagation issues if called from a bubbled event
    setViewerImages(images);
    setCurrentImageIndex(index);
    setViewerOpen(true);
  };

  // Date Filter State (Default: Today - 7 days)
  const today = new Date();
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);

  const [startDate, setStartDate] = useState(toLocalDateString(lastWeek));
  const [endDate, setEndDate] = useState(toLocalDateString(today));
  const [showDateFilter, setShowDateFilter] = useState(false);
  // 구역마다 어느 계획이 어떤 점검표로 도는지. 예전에는 구역이 점검표를 하나
  // 들고 있었지만 이제는 계획의 정거장에 붙으므로 계획 쪽에 물어야 한다.
  const [coverage, setCoverage] = useState(null);

  /**
   * 셋을 따로 부른다.
   *
   * Promise.all 로 묶으면 가장 느린 하나가 끝날 때까지 화면이 안 뜬다. 시설
   * 정보는 곧바로 오는데 점검 통계는 기간 전체를 훑느라 오래 걸린다. 시설
   * 이름과 구역부터 그리고 나머지는 오는 대로 채운다.
   */
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/facilities/${id}`)
      .then((res) => alive && setFacility(res.data))
      .catch((err) => {
        console.error('Error fetching facility:', err);
        if (alive) setError('데이터를 불러오는데 실패했습니다.');
      })
      .finally(() => alive && setLoading(false));

    api
      .get(`/inspection-plans/coverage/facility/${id}`)
      .then((res) => alive && setCoverage(res.data))
      .catch((err) => console.error('Error fetching coverage:', err));

    return () => {
      alive = false;
    };
  }, [id]);

  // 기간이 걸린 것만 따로. 날짜를 바꿀 때 시설 정보까지 다시 부를 이유가 없다.
  // 임차 현황은 기간과 무관하다. 날짜를 바꿀 때마다 다시 부를 이유가 없어
  // 따로 둔다.
  useEffect(() => {
    if (!canManageLeases) return;
    let alive = true;
    api
      .get(`/leases/occupancy/facility/${id}`)
      .then((res) => alive && setOccupancy(res.data))
      .catch((err) => console.error('Error fetching occupancy:', err));
    return () => {
      alive = false;
    };
  }, [id, canManageLeases]);

  useEffect(() => {
    let alive = true;
    api
      .get(`/reports/facility-stats?facilityId=${id}&startDate=${startDate}&endDate=${endDate}`)
      .then((res) => alive && setStats(res.data))
      .catch((err) => console.error('Error fetching facility stats:', err));
    return () => {
      alive = false;
    };
  }, [id, startDate, endDate]);

  const loadReports = useCallback(
    async (page = 1, append = false) => {
      setLoadingReports(true);
      try {
        const { data } = await api.get('/reports', {
          params: {
            facilityId: id,
            type: 'INSPECTION',
            from: startDate,
            to: endDate,
            page,
            // 한 건이 담는 내용이 많다 -- 항목 목록에 사진 넉 장까지. 스무 건이면
            // 화면이 너무 길고, 사진 주소를 서명하는 값도 그만큼 든다.
            limit: 10,
          },
        });
        setInspections((prev) =>
          append ? [...prev, ...(data.items || [])] : data.items || [],
        );
        setReportPage({ page: data.page, total: data.total, limit: data.limit });
        // 쪽이 바뀌면 펼쳐 둔 것도 접는다. 남겨 두면 안 보이는 것이 열려 있다.
        if (!append) setExpanded({});
      } catch (err) {
        console.error('Error fetching inspections:', err);
      } finally {
        setLoadingReports(false);
      }
    },
    [id, startDate, endDate],
  );

  // 기간이 바뀌면 첫 쪽부터 다시. 이어 붙여 두면 앞 기간의 것이 섞인다.
  useEffect(() => {
    loadReports(1, false);
  }, [loadReports]);


  const handleDelete = async () => {
    if (window.confirm('정말 이 시설을 삭제하시겠습니까? 관련 데이터가 모두 삭제됩니다.')) {
      try {
        await api.delete(`/facilities/${id}`);
        showAlert('완료', '시설이 삭제되었습니다.', 'success');
        setTimeout(() => navigate('/facilities'), 1000);
      } catch (err) {
        console.error("Failed to delete facility", err);
        showAlert('오류', '시설 삭제에 실패했습니다.', 'danger');
      }
    }
  };

  if (loading && !facility) return <div className="text-center p-5"><div className="spinner-border text-primary" role="status"></div></div>;
  if (error) return <div className="alert alert-danger m-3">{error}</div>;
  if (!facility) return <div className="alert alert-warning m-3">시설을 찾을 수 없습니다.</div>;

  const sections = facility.sections || [];
  // 어느 순회에도 안 들어간 구역. 손댈 거리가 있다는 것은 보는 자리에서도 알아야
  // 한다. 나머지 -- 어느 계획이 어떤 점검표로 도는지 -- 는 구역 관리에서 본다.
  const uncovered = (coverage?.sections ?? []).filter(
    (c) => c.coveredBy.length === 0,
  );
  // 구역을 도는 계획 이름. 대개 모든 구역에 같은 계획이 걸려 있어서 한 번만
  // 적으면 된다. 어느 구역에 무엇이 걸렸는지는 구역 관리에서 본다.
  const sectionPlanNames = [
    ...new Set(
      (coverage?.sections ?? []).flatMap((c) => c.coveredBy.map((x) => x.planName)),
    ),
  ];
  const { issues } = stats;

  return (
    <div className="facility-detail-page bg-light min-vh-100 pb-5">
      <style>{`
        .back-btn {
          border: none;
          background: none;
          font-size: 1.5rem;
          color: #333;
          margin-right: 1rem;
        }
        .info-card {
          background: white;
          border-radius: 16px;
          padding: 1.25rem 1rem;
          margin: 0 0 1rem 0;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }
        .qr-placeholder {
          width: 120px;
          height: 120px;
          background: #f0f0f0;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .action-btn {
          width: 45px;
          height: 45px;
          border-radius: 12px;
          border: 1px solid #eee;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #555;
          transition: all 0.2s;
          cursor: pointer;
        }
        .action-btn:hover {
          background: #f8f9fa;
        }
        .section-chip {
          background: #f1f3f5;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          color: #495057;
          border: none;
          white-space: nowrap;
          cursor: pointer;
        }
        .section-chip:hover {
          background: #e9ecef;
        }
        .date-filter {
          border: 1px solid #ddd;
          border-radius: 10px;
          padding: 0.5rem 1rem;
          background: white;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: 600;
        }
        .inspection-card {
          transition: transform 0.2s, box-shadow 0.2s;
          cursor: pointer;
        }
        .inspection-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
          background-color: #fcfcfc !important;
        }
      `}</style>

      {/* Header */}
      <div className="page-title-bar w-100">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left"></i>
        </button>
        <h5 className="mb-0 fw-bold text-start">{facility.name}</h5>
      </div>

      {/* Info Card */}
      <div className="info-card">
        <h5 className="fw-bold mb-3">시설 정보</h5>

        <div className="d-flex flex-column gap-3 mb-4">
          <div className="flex-grow-1 d-flex flex-column justify-content-between gap-3">
            <div className="p-3 bg-light rounded-4">
              <div className="d-flex align-items-center text-muted mb-1">
                <i className="bi bi-geo-alt-fill me-2 text-primary small"></i>
                <span className="fw-bold x-small">주소</span>
              </div>
              <div className="fw-bold small text-dark">{facility.address}</div>
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-light border flex-grow-1 rounded-3 py-1.5 fw-bold text-secondary small"
                onClick={() => navigate(`/facilities/${id}/update`)}
              >
                <i className="bi bi-pencil-square me-1"></i> 수정
              </button>
              <button
                className="btn btn-light border flex-grow-1 rounded-3 py-1.5 fw-bold text-danger small"
                onClick={handleDelete}
              >
                <i className="bi bi-trash me-1"></i> 삭제
              </button>
            </div>
          </div>
        </div>

        {/* 이 건물을 무엇이 도는가. 구역 밑에 넣어 두면 어느 쪽이 계획이고 어느
            쪽이 구역인지 알아볼 수 없어서 제목을 따로 단다. */}
        <div className="mb-3">
          <label className="fw-bold text-muted small mb-2 d-block">계획</label>
          {coverage &&
          coverage.wholeFacility.length === 0 &&
          sectionPlanNames.length === 0 ? (
            <span className="text-danger small">이 시설을 도는 계획이 없습니다.</span>
          ) : (
            <div style={{ fontSize: '0.8rem' }}>
              {coverage?.wholeFacility.map((c, i) => (
                <div key={`w-${i}`}>
                  <span className="fw-medium">{c.planName}</span>
                  <span className="text-muted">
                    {' · 건물 전체를 한 장으로'}
                    {c.templateName ? ` · ${c.templateName}` : ''}
                  </span>
                </div>
              ))}
              {sectionPlanNames.length > 0 && (
                <div>
                  <span className="fw-medium">{sectionPlanNames.join(', ')}</span>
                  <span className="text-muted"> · 구역마다</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 여기는 보는 곳이다. 어느 계획이 어떤 점검표로 도는지는 고칠 때 볼
            정보라 '구역 관리' 로 옮겼다. 이름을 나란히 늘어놓기만 하면 어느
            쪽이 구역이고 어느 쪽이 점검표인지 알아볼 수 없었다 -- 점검표
            이름이 구역 이름과 비슷하기 때문이다. */}
        <div className="mb-2">
          <div className="d-flex align-items-baseline gap-2 mb-2">
            <label className="fw-bold text-muted small mb-0">구역</label>
            <span className="text-muted small">{sections.length}곳</span>
            <button
              className="btn btn-sm btn-link text-decoration-none p-0 ms-auto"
              onClick={() => navigate(`/facilities/${id}/edit`)}
            >
              구역 관리
            </button>
          </div>

          {sections.length === 0 ? (
            <span className="text-muted small">등록된 구역이 없습니다.</span>
          ) : (
            <div className="d-flex flex-wrap gap-1">
              {sections.map((sec) => (
                <span
                  key={sec.id}
                  className="badge bg-light text-dark border fw-normal"
                  style={{ fontSize: '0.78rem' }}
                >
                  {sec.name}
                </span>
              ))}
            </div>
          )}

          {/* 빠진 구역만 여기 남긴다. 손댈 거리가 있다는 것은 보는 자리에서도
              알아야 하고, 나머지는 관리 화면에서 본다. */}
          {uncovered.length > 0 && (
            <div className="text-danger mt-2" style={{ fontSize: '0.78rem' }}>
              <i className="bi bi-exclamation-triangle me-1"></i>
              {uncovered.map((c) => c.name).join(', ')} 은(는) 어느 순회에도 들어 있지 않습니다.
            </div>
          )}
        </div>
      </div>

      {/* Date Filter */}
      <div className="mb-3 position-relative">
        <div
          className="date-filter cursor-pointer"
          onClick={() => setShowDateFilter(!showDateFilter)}
          style={{ cursor: 'pointer' }}
        >
          <span><i className="bi bi-calendar-range me-2"></i> {startDate} ~ {endDate}</span>
          <i className={`bi bi-chevron-${showDateFilter ? 'up' : 'down'}`}></i>
        </div>

        {showDateFilter && (
          <div className="position-absolute start-0 end-0 mt-2 bg-white p-3 rounded-4 shadow z-3 border">
            <div className="d-flex gap-2 mb-2">
              <div className="flex-grow-1">
                <label className="small text-muted mb-1">시작일</label>
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="flex-grow-1">
                <label className="small text-muted mb-1">종료일</label>
                <input
                  type="date"
                  className="form-control"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            <button className="btn btn-primary w-100 btn-sm" onClick={() => setShowDateFilter(false)}>닫기</button>
          </div>
        )}
      </div>

      {/* 임차 현황. 계약은 점검과 다른 일이라 카드를 따로 둔다 -- 이 건물이
          어떻게 관리되는가와 누가 빌려 쓰는가는 묻는 사람이 다르다. */}
      {/* 호실이 없어도 낸다. 호실 관리로 들어가는 길이 이 카드 안에만 있어서,
          비어 있을 때 숨기면 첫 호실을 만들 방법이 없어진다. */}
      {canManageLeases && occupancy && (
        <div className="info-card">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-bold mb-0">임차 현황</h6>
            <Link
              to={`/facilities/${id}/units`}
              className="text-decoration-none small"
            >
              호실 관리
            </Link>
          </div>
          {occupancy.total === 0 ? (
            <div className="text-muted small">
              빌려줄 곳이 아직 없습니다. 「호실 관리」에서 호실이나 층 전체를 더해 주세요.
            </div>
          ) : (
          <>
          <div className="d-flex gap-2 mb-3">
            <span className="badge bg-primary bg-opacity-10 text-primary p-2 px-3 rounded-3">
              임대 중 {occupancy.occupied}
            </span>
            <span className="badge bg-secondary bg-opacity-10 text-secondary p-2 px-3 rounded-3">
              공실 {occupancy.vacant}
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {occupancy.units.map((u) => (
              <span
                key={u.id}
                className={`badge rounded-pill fw-normal ${u.lease ? 'bg-light text-dark border' : 'bg-white text-muted border'}`}
                style={{ fontSize: '0.75rem' }}
                title={u.lease ? u.lease.tenantName : '공실'}
              >
                {u.name}
                {u.lease && <span className="ms-1 text-primary">· {u.lease.tenantName}</span>}
              </span>
            ))}
          </div>
          </>
          )}
        </div>
      )}

      {/* Issue Stats */}
      <div className="info-card">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div
            className="d-flex align-items-center gap-2"
            onClick={() => setIsIssueModalOpen(true)}
            style={{ cursor: 'pointer' }}
          >
            <h6 className="fw-bold mb-0">이슈 {issues.total}</h6>
            <i className="bi bi-chevron-right text-muted small"></i>
          </div>
        </div>
        <div className="d-flex gap-2">
          <span className="badge bg-danger bg-opacity-10 text-danger p-2 px-3 rounded-3">처리 대기 중 {issues.unresolved}</span>
          <span className="badge bg-primary bg-opacity-10 text-primary p-2 px-3 rounded-3">해결 완료 {issues.resolved}</span>
        </div>
      </div>

      <div className="mb-2">
        {/* 전체 수를 낸다. 지금 화면에 몇 개가 있는지가 아니라 이 기간에 몇 개가
            있는지가 알고 싶은 값이다. */}
        {/* 기록이 하나 안 보인다 싶을 때 찾아 들어갈 곳. 지운 기록은 사라지지
            않고 남아 있는데, 지금까지 그리로 가는 길이 화면에 없었다. */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-bold mb-0">점검 내역 {reportPage.total}</h6>
          {canManageReports && (
            <Link
              to={`/inspections/deleted?facilityId=${id}`}
              className="text-decoration-none text-muted"
              style={{ fontSize: '0.78rem' }}
            >
              <i className="bi bi-trash3 me-1"></i>
              지운 기록
            </Link>
          )}
        </div>
        {inspections.length > 0 ? (
          inspections.map((insp) => {
            const open = expanded[insp.id];
            return (
              <div key={insp.id} className="bg-white rounded-4 mb-2 shadow-sm">
                {/* 한 줄 요약. 목록의 쓸모는 훑기다 -- 이 기간에 무엇이 있었나,
                    빠진 건 없나. 한 건이 화면을 다 차지하면 훑을 수가 없다. */}
                <div
                  className="d-flex align-items-center gap-2 p-3"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleReport(insp.id)}
                >
                  <div style={{ minWidth: 0 }} className="flex-grow-1">
                    <div className="small text-truncate">
                      <span className="badge bg-light text-dark border me-2">
                        {insp.section?.name ?? '건물 전체'}
                      </span>
                      {insp.reporter?.name}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {new Date(insp.createdAt).toLocaleString()}
                      {insp.photoCount > 0 && ` · 사진 ${insp.photoCount}`}
                    </div>
                  </div>
                  <i className={`bi bi-chevron-${open ? 'up' : 'down'} text-muted small`}></i>
                </div>

                {open && (
                  <div className="px-3 pb-3">
                    <div className="bg-light p-3 rounded-3 mb-2 text-break small text-muted">
                      {formatDescription(insp.description) || '내용 없음'}
                    </div>

                    {/* 사진은 펼칠 때 그 한 건만 가져온다. 목록은 몇 장인지만 안다. */}
                    {open.loading ? (
                      <div className="text-muted small">사진 불러오는 중…</div>
                    ) : (
                      open.photoUrls?.length > 0 && (
                        <div className="d-flex gap-2 overflow-auto pb-2">
                          {open.photoUrls.map((url, idx) => (
                            <img
                              key={idx}
                              src={url}
                              alt={`점검 사진 ${idx + 1}`}
                              className="rounded-3 border"
                              style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                cursor: 'pointer',
                              }}
                              onClick={() => openViewer(open.photoUrls, idx)}
                            />
                          ))}
                        </div>
                      )
                    )}

                    <button
                      className="btn btn-sm btn-link text-decoration-none px-0"
                      onClick={() => navigate(`/inspections/${insp.id}`)}
                    >
                      기록 페이지로 열기
                      <i className="bi bi-chevron-right ms-1" style={{ fontSize: '0.7rem' }}></i>
                    </button>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="text-center text-muted py-4 bg-white rounded-4">
            <small>
              {loadingReports ? '불러오는 중…' : '해당 기간 점검 내역이 없습니다.'}
            </small>
          </div>
        )}

        <Pagination
          page={reportPage.page}
          total={reportPage.total}
          limit={reportPage.limit}
          loading={loadingReports}
          onChange={loadReports}
        />
      </div>



      <ImageViewerModal
        show={viewerOpen}
        onHide={() => setViewerOpen(false)}
        images={viewerImages}
        initialIndex={currentImageIndex}
      />

      <IssueSliderModal
        show={isIssueModalOpen}
        onHide={() => setIsIssueModalOpen(false)}
        issues={issues.list || []}
      />
    </div>
  );
};

export default FacilityDetailPage;